import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  FolderOpen, Folder, PartyPopper, MessageCircle, Save, AlertTriangle,
  Rocket, RefreshCw, Pencil, Trash2, Clock, X,
} from "lucide-react";
import { encoderLabel } from "../../services/videoCompressionService";
import UploadStatusIcon from "../../components/common/UploadStatusIcon";
import { useAutoUpload } from "../../context/AutoUploadContext";
import { useUpload } from "../../context/UploadContext";
import { useAuth } from "../../context/AuthContext";
import { getEvents } from "../../services/eventService";
import { getGalleriesByEvent } from "../../services/galleryService";
import { getMyChatRooms } from "../../services/chatService";
import { getLatestPSSVersion } from "../../services/configService";
import Button from "../../components/common/Button";
import Loader from "../../components/common/Loader";
import CreateGalleryModal from "../../components/common/CreateGalleryModal";
import CreateAlbumModal from "../../components/common/CreateAlbumModal";
import PSSUpdateBanner from "../../components/common/PSSUpdateBanner";
import { formatFileSize, resolveUserCountryCode } from "../../utils/helpers";
import { toast } from "react-toastify";
import PrivacyPolicyModal, { hasAcceptedPrivacy } from "../../components/common/PrivacyPolicyModal";

const INTERVAL_KEYS = [
  { key: "autoUpload.interval.min1", value: 1 * 60 * 1000 },
  { key: "autoUpload.interval.min5", value: 5 * 60 * 1000 },
  { key: "autoUpload.interval.min15", value: 15 * 60 * 1000 },
  { key: "autoUpload.interval.min30", value: 30 * 60 * 1000 },
  { key: "autoUpload.interval.hour1", value: 60 * 60 * 1000 },
  { key: "autoUpload.interval.hour2", value: 2 * 60 * 60 * 1000 },
  { key: "autoUpload.interval.hour4", value: 4 * 60 * 60 * 1000 },
];

const getIntervalOptions = (t) => INTERVAL_KEYS.map((o) => ({ label: t(o.key), value: o.value }));

const isFileSystemSupported = typeof window !== "undefined" && "showDirectoryPicker" in window;

const formatTime = (date) => {
  if (!date) return "—";
  const d = new Date(date);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString("en-US", {
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  if (isToday) return time;
  const dateStr = d.toLocaleDateString("en-US", {
    month: "short", day: "numeric",
  });
  return `${dateStr}, ${time}`;
};

const formatInterval = (t, ms) => {
  const opt = INTERVAL_KEYS.find((o) => o.value === ms);
  return opt ? t(opt.key) : t("autoUpload.interval.fallback", { count: Math.round(ms / 60000) });
};

const formatEta = (t, seconds) => {
  if (seconds == null) return "";
  if (seconds < 5) return t("autoUpload.eta.fewSeconds");
  if (seconds < 60) return t("autoUpload.eta.seconds", { seconds });
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return secs > 0 ? t("autoUpload.eta.minutesSeconds", { minutes: mins, seconds: secs }) : t("autoUpload.eta.minutes", { minutes: mins });
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins > 0 ? t("autoUpload.eta.hoursMinutes", { hours: hrs, minutes: remMins }) : t("autoUpload.eta.hours", { hours: hrs });
};

const compressionStatusText = (t, item) => {
  const pct = Math.max(0, Math.min(100, Number(item.compressProgress || 0)));
  if (item.fileType?.startsWith("video/") && pct <= 30) {
    return t("autoUpload.fileStatus.copyingToStudio", { percent: pct });
  }
  return t("autoUpload.fileStatus.compressing", { percent: pct });
};

const fileStatusText = (t, item) => {
  switch (item.status) {
    case "checking": return t("autoUpload.fileStatus.analysing");
    case "compressing": return compressionStatusText(t, item);
    case "uploading": return t("autoUpload.fileStatus.uploading", { percent: item.uploadProgress, eta: item.eta ? " — " + formatEta(t, item.eta) : "" });
    case "retrying": return t("autoUpload.fileStatus.retrying");
    case "done": return t("autoUpload.fileStatus.done");
    case "failed": return item.error || t("autoUpload.fileStatus.failed");
    default: return item.queuePosition ? t("autoUpload.fileStatus.queuePosition", { position: item.queuePosition }) : t("autoUpload.fileStatus.waiting");
  }
};

/* ─── Edit Job Modal ─── */
const EditJobModal = ({ job, onClose, onSave }) => {
  const { t } = useTranslation(["autoUpload", "common"]);
  const editIntervalOptions = getIntervalOptions(t);
  const [editName, setEditName] = useState(job.jobName || "");
  const [editSourceType, setEditSourceType] = useState(job.sourceType || "event");
  const [editEvents, setEditEvents] = useState([]);
  const [editRooms, setEditRooms] = useState([]);
  const [editSourceId, setEditSourceId] = useState(job.sourceId || "");
  const [editGalleries, setEditGalleries] = useState([]);
  const [editGalleryId, setEditGalleryId] = useState(String(job.gallery?.Id || ""));
  const [editAlbumId, setEditAlbumId] = useState(String(job.album?.Id || ""));
  const [editIntervalMs, setEditIntervalMs] = useState(job.intervalMs);
  const [editDirHandle, setEditDirHandle] = useState(null);
  const [editFolderName, setEditFolderName] = useState("");
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loadingGalleries, setLoadingGalleries] = useState(false);
  const [showCreateGallery, setShowCreateGallery] = useState(false);
  const [showCreateAlbum, setShowCreateAlbum] = useState(false);

  // Load events & rooms on mount
  useEffect(() => {
    setLoadingEvents(true);
    getEvents()
      .then((res) => setEditEvents(res.data?.Result || res.data || []))
      .catch(() => {})
      .finally(() => setLoadingEvents(false));

    setLoadingRooms(true);
    getMyChatRooms("Personal")
      .then((res) => setEditRooms(res.data?.Result || res.data || []))
      .catch(() => {})
      .finally(() => setLoadingRooms(false));
  }, []);

  // Load galleries when source changes
  useEffect(() => {
    if (!editSourceId) { setEditGalleries([]); return; }
    setLoadingGalleries(true);
    getGalleriesByEvent(editSourceId)
      .then((res) => {
        const gals = res.data?.Result || [];
        setEditGalleries(gals);
        // If current gallery exists in new list, keep it; otherwise reset
        if (!gals.find((g) => String(g.Id) === editGalleryId)) {
          setEditGalleryId("");
          setEditAlbumId("");
        }
      })
      .catch(() => toast.error(t("autoUpload.toasts.failedLoadGalleries")))
      .finally(() => setLoadingGalleries(false));
  }, [editSourceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset album when gallery changes
  useEffect(() => {
    const gal = editGalleries.find((g) => String(g.Id) === editGalleryId);
    if (gal) {
      const albumExists = (gal.Albums || []).find((a) => String(a.Id) === editAlbumId);
      if (!albumExists) setEditAlbumId("");
    }
  }, [editGalleryId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset source selection when source type changes (but not on initial render)
  const initialRef = useRef(true);
  useEffect(() => {
    if (initialRef.current) { initialRef.current = false; return; }
    setEditSourceId("");
    setEditGalleries([]);
    setEditGalleryId("");
    setEditAlbumId("");
  }, [editSourceType]);

  const editSelectedGallery = editGalleries.find((g) => String(g.Id) === editGalleryId);
  const editAlbums = editSelectedGallery?.Albums || [];
  const editSelectedAlbum = editAlbums.find((a) => String(a.Id) === editAlbumId);

  const handleChangeFolder = async () => {
    try {
      const handle = await window.showDirectoryPicker({ mode: "read" });
      setEditDirHandle(handle);
      setEditFolderName(handle.name);
    } catch (err) {
      if (err.name !== "AbortError") toast.error(t("autoUpload.toasts.failedSelectFolder"));
    }
  };

  const handleSave = () => {
    const updates = { jobId: job.id, jobName: editName.trim() || job.jobName, intervalMs: editIntervalMs };

    if (editDirHandle) {
      updates.dirHandle = editDirHandle;
      updates.folderName = editFolderName;
    }

    if (editSelectedGallery && editSelectedAlbum) {
      updates.gallery = editSelectedGallery;
      updates.album = editSelectedAlbum;
      updates.chatRoomId = editSelectedGallery.ChatRoomId || editSourceId;
      updates.sourceType = editSourceType;
      updates.sourceId = editSourceId;
    }

    onSave(updates);
  };

  // Did anything actually change?
  const galleryChanged = editSelectedGallery && editSelectedAlbum &&
    (String(editSelectedGallery.Id) !== String(job.gallery?.Id) || String(editSelectedAlbum.Id) !== String(job.album?.Id));
  const hasChanges = editName.trim() !== job.jobName || editIntervalMs !== job.intervalMs || editDirHandle || galleryChanged;

  return (
    <div style={editStyles.overlay} onClick={onClose}>
      <div style={editStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={editStyles.header}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#1e1e2d" }}>{t("autoUpload.editModal.title")}</h3>
          <button style={editStyles.closeBtn} onClick={onClose}><X size={16} strokeWidth={2} /></button>
        </div>

        {/* Job Name */}
        <div style={styles.field}>
          <label style={styles.label}>{t("autoUpload.editModal.jobNameLabel")}</label>
          <input
            style={styles.select}
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
          />
        </div>

        {/* Folder */}
        <div style={styles.field}>
          <label style={styles.label}>{t("autoUpload.editModal.localFolderLabel")}</label>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <Button onClick={handleChangeFolder} variant="secondary" icon={FolderOpen} style={{ padding: "8px 16px", fontSize: 13 }}>
              {t("autoUpload.changeFolder")}
            </Button>
            <span style={styles.folderName}>
              {editDirHandle ? editFolderName : job.folderName}
            </span>
          </div>
        </div>

        {/* Current destination (info-only) */}
        <div style={{ ...styles.field, padding: "10px 14px", background: "#f8f8fc", borderRadius: 8, marginBottom: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", marginBottom: 4 }}>
            {t("autoUpload.editModal.currentDestinationLabel")}
          </div>
          <div style={{ fontSize: 14, color: "#333" }}>
            {job.gallery?.Name} → {job.album?.Name}
          </div>
        </div>

        {/* Source Type Toggle */}
        <div style={styles.field}>
          <label style={styles.label}>{t("autoUpload.editModal.changeDestinationLabel")}</label>
          <div style={styles.toggleGroup}>
            <button
              type="button"
              style={{ ...styles.toggleBtn, ...(editSourceType === "event" ? styles.toggleBtnActive : {}) }}
              onClick={() => setEditSourceType("event")}
            >
              <PartyPopper size={13} strokeWidth={2.25} /> {t("autoUpload.editModal.eventTab")}
            </button>
            <button
              type="button"
              style={{ ...styles.toggleBtn, ...(editSourceType === "room" ? styles.toggleBtnActive : {}) }}
              onClick={() => setEditSourceType("room")}
            >
              <MessageCircle size={13} strokeWidth={2.25} /> {t("autoUpload.editModal.roomTab")}
            </button>
          </div>
        </div>

        {/* Event / Room dropdown */}
        {editSourceType === "event" ? (
          <div style={styles.field}>
            <label style={styles.label}>{t("autoUpload.editModal.eventLabel")}</label>
            <select
              style={styles.select}
              value={editSourceId}
              onChange={(e) => setEditSourceId(e.target.value)}
              disabled={loadingEvents}
            >
              <option value="">{loadingEvents ? t("autoUpload.loadingGalleries") : t("autoUpload.selectEventPlaceholder")}</option>
              {editEvents.map((ev) => (
                <option key={ev.Id} value={ev.Id}>{ev.Name}</option>
              ))}
            </select>
          </div>
        ) : (
          <div style={styles.field}>
            <label style={styles.label}>{t("autoUpload.editModal.roomLabel")}</label>
            <select
              style={styles.select}
              value={editSourceId}
              onChange={(e) => setEditSourceId(e.target.value)}
              disabled={loadingRooms}
            >
              <option value="">{loadingRooms ? t("autoUpload.loadingGalleries") : t("autoUpload.selectRoomPlaceholder")}</option>
              {editRooms.map((r) => (
                <option key={r.Id} value={r.Id}>{r.Name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Gallery */}
        <div style={styles.field}>
          <label style={styles.label}>{t("autoUpload.galleryLabel")}</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              style={{ ...styles.select, flex: 1 }}
              value={editGalleryId}
              onChange={(e) => setEditGalleryId(e.target.value)}
              disabled={!editSourceId || loadingGalleries}
            >
              <option value="">
                {!editSourceId
                  ? t("autoUpload.selectSourceFirst", { source: editSourceType === "event" ? t("autoUpload.sourceEvent") : t("autoUpload.sourceRoom") })
                  : loadingGalleries ? t("autoUpload.loadingGalleries") : t("autoUpload.selectGalleryPlaceholder")}
              </option>
              {editGalleries.map((g) => (
                <option key={g.Id} value={g.Id}>{g.Name}</option>
              ))}
            </select>
            {editSourceId && (
              <Button
                variant="secondary"
                onClick={() => setShowCreateGallery(true)}
                style={styles.inlineCreateBtn}
                title={t("autoUpload.createGalleryTitle")}
              >
                {t("autoUpload.newButton")}
              </Button>
            )}
          </div>
        </div>

        {/* Album */}
        <div style={styles.field}>
          <label style={styles.label}>{t("autoUpload.albumLabel")}</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              style={{ ...styles.select, flex: 1 }}
              value={editAlbumId}
              onChange={(e) => setEditAlbumId(e.target.value)}
              disabled={!editGalleryId || editAlbums.length === 0}
            >
              <option value="">
                {!editGalleryId
                  ? t("autoUpload.selectGalleryFirstPlaceholder")
                  : editAlbums.length === 0
                  ? t("autoUpload.noAlbumsPlaceholder")
                  : t("autoUpload.selectAlbumPlaceholder")}
              </option>
              {editAlbums.map((a) => (
                <option key={a.Id} value={a.Id}>{a.Name} ({t("autoUpload.filesUnit", { count: a.ImageCount || 0 })})</option>
              ))}
            </select>
            {editGalleryId && (
              <Button
                variant="secondary"
                onClick={() => setShowCreateAlbum(true)}
                style={styles.inlineCreateBtn}
                title={t("autoUpload.createAlbumTitle")}
              >
                {t("autoUpload.newButton")}
              </Button>
            )}
          </div>
        </div>

        {/* Interval */}
        <div style={styles.field}>
          <label style={styles.label}>{t("autoUpload.scanIntervalLabel")}</label>
          <select
            style={styles.select}
            value={editIntervalMs}
            onChange={(e) => setEditIntervalMs(Number(e.target.value))}
          >
            {editIntervalOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <Button variant="secondary" onClick={onClose} style={{ flex: 1 }}>
            {t("common:actions.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges} icon={Save} style={{ flex: 1 }}>
            {t("autoUpload.editModal.saveChanges")}
          </Button>
        </div>

        {/* Create Gallery Modal (inside edit) */}
        {showCreateGallery && (
          <CreateGalleryModal
            eventId={editSourceId}
            onClose={() => setShowCreateGallery(false)}
            onCreated={(newGallery) => {
              setLoadingGalleries(true);
              getGalleriesByEvent(editSourceId)
                .then((res) => {
                  const updated = res.data?.Result || [];
                  setEditGalleries(updated);
                  const created = updated.find((g) => g.Name === newGallery?.Name) || updated[updated.length - 1];
                  if (created) setEditGalleryId(String(created.Id));
                })
                .catch(() => toast.error(t("autoUpload.toasts.failedRefreshGalleries")))
                .finally(() => setLoadingGalleries(false));
            }}
          />
        )}

        {/* Create Album Modal (inside edit) */}
        {showCreateAlbum && editSelectedGallery && (
          <CreateAlbumModal
            galleryId={editSelectedGallery.Id}
            galleryName={editSelectedGallery.Name}
            onClose={() => setShowCreateAlbum(false)}
            onCreated={(newAlbum) => {
              setLoadingGalleries(true);
              getGalleriesByEvent(editSourceId)
                .then((res) => {
                  const updated = res.data?.Result || [];
                  setEditGalleries(updated);
                  const gal = updated.find((g) => String(g.Id) === editGalleryId);
                  if (gal) {
                    const created = (gal.Albums || []).find((a) => a.Name === newAlbum?.Name)
                      || (gal.Albums || [])[(gal.Albums || []).length - 1];
                    if (created) setEditAlbumId(String(created.Id));
                  }
                })
                .catch(() => toast.error(t("autoUpload.toasts.failedRefreshAlbums")))
                .finally(() => setLoadingGalleries(false));
            }}
          />
        )}
      </div>
    </div>
  );
};

const editStyles = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, padding: 20,
  },
  modal: {
    background: "#fff", borderRadius: 14, padding: 28, width: "100%",
    maxWidth: 520, maxHeight: "90vh", overflowY: "auto",
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    marginBottom: 22, paddingBottom: 14, borderBottom: "1px solid #f0f0f0",
  },
  closeBtn: {
    background: "none", border: "none", fontSize: 18, color: "#999",
    cursor: "pointer", padding: "4px 8px", borderRadius: 6,
  },
};

const AutoUpload = () => {
  const { t } = useTranslation(["autoUpload", "common"]);
  const intervalOptions = getIntervalOptions(t);
  const { user } = useAuth();
  const { jobs, loaded, createJob, updateJob, pauseJob, resumeJob, removeJob, scanNow } = useAutoUpload();
  const { sessions } = useUpload();

  // PSS version check
  const [pssInstalledVersion, setPssInstalledVersion] = useState(null);
  const [pssLatestVersion, setPssLatestVersion] = useState(null);
  useEffect(() => {
    getLatestPSSVersion()
      .then((res) => { if (res.data?.version) setPssLatestVersion(res.data.version); })
      .catch(() => {});
    import("../../services/videoCompressionService").then((mod) => {
      mod.checkCompanion().then(() => {
        setPssInstalledVersion(mod.getCompanionVersion?.() ?? null);
      });
    });
  }, []);

  // Setup form state
  const [jobName, setJobName] = useState("");
  const [dirHandle, setDirHandle] = useState(null);
  const [folderName, setFolderName] = useState("");
  const [sourceType, setSourceType] = useState("event"); // "event" | "room"
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [rooms, setRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState("");
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [galleries, setGalleries] = useState([]);
  const [selectedGalleryId, setSelectedGalleryId] = useState("");
  const [selectedAlbumId, setSelectedAlbumId] = useState("");
  const [intervalMs, setIntervalMs] = useState(INTERVAL_KEYS[1].value); // default 30 min
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingGalleries, setLoadingGalleries] = useState(false);
  const [showCreateGallery, setShowCreateGallery] = useState(false);
  const [showCreateAlbum, setShowCreateAlbum] = useState(false);

  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [editingJob, setEditingJob] = useState(null); // job object being edited
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // Track completed session groups to hide progress after 3s
  const [hiddenProgress, setHiddenProgress] = useState({}); // { [jobId]: true }

  // Countdown timers — re-render every second
  const [, setTick] = useState(0);
  const hasRunningJobs = Object.values(jobs).some((j) => j.status === "running");
  useEffect(() => {
    if (!hasRunningJobs) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [hasRunningJobs]);

  // The active source ID (either eventId or roomId)
  const activeSourceId = sourceType === "event" ? selectedEventId : selectedRoomId;

  // Load events on mount
  useEffect(() => {
    setLoadingEvents(true);
    getEvents()
      .then((res) => setEvents(res.data?.Result || res.data || []))
      .catch(() => toast.error(t("autoUpload.toasts.failedLoadEvents")))
      .finally(() => setLoadingEvents(false));
  }, []);

  // Load rooms on mount
  useEffect(() => {
    setLoadingRooms(true);
    getMyChatRooms("Personal")
      .then((res) => setRooms(res.data?.Result || res.data || []))
      .catch(() => toast.error(t("autoUpload.toasts.failedLoadRooms")))
      .finally(() => setLoadingRooms(false));
  }, []);

  // Reset selections when source type changes
  useEffect(() => {
    setSelectedEventId("");
    setSelectedRoomId("");
    setGalleries([]);
    setSelectedGalleryId("");
    setSelectedAlbumId("");
  }, [sourceType]);

  // Load galleries when source changes (event or room)
  useEffect(() => {
    if (!activeSourceId) { setGalleries([]); return; }
    setLoadingGalleries(true);
    setSelectedGalleryId("");
    setSelectedAlbumId("");
    getGalleriesByEvent(activeSourceId)
      .then((res) => setGalleries(res.data?.Result || []))
      .catch(() => toast.error(t("autoUpload.toasts.failedLoadGalleries")))
      .finally(() => setLoadingGalleries(false));
  }, [activeSourceId]);

  // Reset album when gallery changes
  useEffect(() => { setSelectedAlbumId(""); }, [selectedGalleryId]);

  const selectedGallery = galleries.find((g) => String(g.Id) === String(selectedGalleryId));
  const albums = selectedGallery?.Albums || [];
  const selectedAlbum = albums.find((a) => String(a.Id) === String(selectedAlbumId));
  const countryCode = resolveUserCountryCode(user, null);

  const handleSelectFolder = async () => {
    try {
      const handle = await window.showDirectoryPicker({ mode: "read" });
      setDirHandle(handle);
      setFolderName(handle.name);
    } catch (err) {
      if (err.name !== "AbortError") {
        toast.error(t("autoUpload.toasts.failedSelectFolder"));
      }
    }
  };

  const canStart = dirHandle && selectedGallery && selectedAlbum && intervalMs;

  const doCreateJob = () => {
    if (!canStart) return;

    createJob({
      jobName: jobName.trim() || folderName,
      dirHandle,
      folderName,
      gallery: selectedGallery,
      album: selectedAlbum,
      chatRoomId: selectedGallery.ChatRoomId || activeSourceId,
      countryCode,
      intervalMs,
      sourceType,
      sourceId: activeSourceId,
    });

    // Reset form
    setJobName("");
    setDirHandle(null);
    setFolderName("");
    setSelectedEventId("");
    setSelectedRoomId("");
    setSelectedGalleryId("");
    setSelectedAlbumId("");
  };

  const handleStart = () => {
    if (!canStart) return;
    if (hasAcceptedPrivacy(user?.Email)) {
      doCreateJob();
    } else {
      setShowPrivacyModal(true);
    }
  };

  const getNextScanIn = useCallback((job) => {
    if (!job.lastScanTime || job.status !== "running") return null;
    const nextScanAt = new Date(job.lastScanTime).getTime() + job.intervalMs;
    const remaining = Math.max(0, Math.ceil((nextScanAt - Date.now()) / 1000));
    if (remaining <= 0) return t("autoUpload.nextScanAnyMoment");
    if (remaining < 60) return `${remaining}s`;
    const mins = Math.floor(remaining / 60);
    const secs = remaining % 60;
    if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const remMins = mins % 60;
    return remMins > 0 ? `${hrs}h ${remMins}m` : `${hrs}h`;
  }, [t]);

  const jobList = Object.values(jobs);

  if (!loaded) {
    return (
      <div>
        <h2 style={styles.heading}>{t("autoUpload.heading")}</h2>
        <Loader />
      </div>
    );
  }

  if (!isFileSystemSupported) {
    return (
      <div>
        <h2 style={styles.heading}>{t("autoUpload.heading")}</h2>
        <div style={styles.warningCard}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 12, color: "#d97706" }}><AlertTriangle size={30} strokeWidth={1.75} /></div>
          <h3 style={{ margin: "0 0 8px", color: "#b45309" }}>{t("autoUpload.browserNotSupported.title")}</h3>
          <p style={{ margin: 0, color: "#92400e", fontSize: 14 }}>
            {t("autoUpload.browserNotSupported.part1")}
            <strong> {t("autoUpload.browserNotSupported.chrome")}</strong> {t("autoUpload.browserNotSupported.or")} <strong>{t("autoUpload.browserNotSupported.edge")}</strong>.
            {t("autoUpload.browserNotSupported.part2")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {showPrivacyModal && (
        <PrivacyPolicyModal
          onAccept={() => { setShowPrivacyModal(false); doCreateJob(); }}
          onDecline={() => setShowPrivacyModal(false)}
        />
      )}
    <div>
      <h2 style={styles.heading}>{t("autoUpload.heading")}</h2>
      <PSSUpdateBanner installedVersion={pssInstalledVersion} latestVersion={pssLatestVersion} />
      <p style={styles.description}>
        {t("autoUpload.description")}
      </p>

      {/* Setup Card */}
      <div style={styles.setupCard}>
        <h3 style={styles.cardTitle}>{t("autoUpload.newJobTitle")}</h3>

        {/* Job Name */}
        <div style={styles.field}>
          <label style={styles.label}>{t("autoUpload.jobNameLabel")}</label>
          <input
            style={styles.select}
            type="text"
            placeholder={t("autoUpload.jobNamePlaceholder")}
            value={jobName}
            onChange={(e) => setJobName(e.target.value)}
          />
        </div>

        {/* Folder Selection */}
        <div style={styles.field}>
          <label style={styles.label}>{t("autoUpload.localFolderLabel")}</label>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <Button onClick={handleSelectFolder} variant="secondary" icon={dirHandle ? FolderOpen : Folder} style={{ padding: "8px 16px", fontSize: 13 }}>
              {dirHandle ? t("autoUpload.changeFolder") : t("autoUpload.selectFolder")}
            </Button>
            {folderName && (
              <span style={styles.folderName}>{folderName}</span>
            )}
          </div>
        </div>

        {/* Source Type Toggle */}
        <div style={styles.field}>
          <label style={styles.label}>{t("autoUpload.uploadToLabel")}</label>
          <div style={styles.toggleGroup}>
            <button
              type="button"
              style={{
                ...styles.toggleBtn,
                ...(sourceType === "event" ? styles.toggleBtnActive : {}),
              }}
              onClick={() => setSourceType("event")}
            >
              <PartyPopper size={13} strokeWidth={2.25} /> {t("autoUpload.eventTab")}
            </button>
            <button
              type="button"
              style={{
                ...styles.toggleBtn,
                ...(sourceType === "room" ? styles.toggleBtnActive : {}),
              }}
              onClick={() => setSourceType("room")}
            >
              <MessageCircle size={13} strokeWidth={2.25} /> {t("autoUpload.roomTab")}
            </button>
          </div>
        </div>

        {/* Event Selection */}
        {sourceType === "event" && (
          <div style={styles.field}>
            <label style={styles.label}>{t("autoUpload.eventLabel")}</label>
            <select
              style={styles.select}
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
              disabled={loadingEvents}
            >
              <option value="">{loadingEvents ? t("autoUpload.loadingEvents") : t("autoUpload.selectEventPlaceholder")}</option>
              {events.map((ev) => (
                <option key={ev.Id} value={ev.Id}>{ev.Name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Room Selection */}
        {sourceType === "room" && (
          <div style={styles.field}>
            <label style={styles.label}>{t("autoUpload.roomLabel")}</label>
            <select
              style={styles.select}
              value={selectedRoomId}
              onChange={(e) => setSelectedRoomId(e.target.value)}
              disabled={loadingRooms}
            >
              <option value="">{loadingRooms ? t("autoUpload.loadingRooms") : t("autoUpload.selectRoomPlaceholder")}</option>
              {rooms.map((r) => (
                <option key={r.Id} value={r.Id}>{r.Name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Gallery Selection */}
        <div style={styles.field}>
          <label style={styles.label}>{t("autoUpload.galleryLabel")}</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              style={{ ...styles.select, flex: 1 }}
              value={selectedGalleryId}
              onChange={(e) => setSelectedGalleryId(e.target.value)}
              disabled={!activeSourceId || loadingGalleries}
            >
              <option value="">
                {!activeSourceId
                  ? t("autoUpload.selectSourceFirst", { source: sourceType === "event" ? t("autoUpload.sourceEvent") : t("autoUpload.sourceRoom") })
                  : loadingGalleries ? t("autoUpload.loadingGalleries") : t("autoUpload.selectGalleryPlaceholder")}
              </option>
              {galleries.map((g) => (
                <option key={g.Id} value={g.Id}>{g.Name}</option>
              ))}
            </select>
            {activeSourceId && (
              <Button
                variant="secondary"
                onClick={() => setShowCreateGallery(true)}
                style={styles.inlineCreateBtn}
                title={t("autoUpload.createGalleryTitle")}
              >
                {t("autoUpload.newButton")}
              </Button>
            )}
          </div>
        </div>

        {/* Album Selection */}
        <div style={styles.field}>
          <label style={styles.label}>{t("autoUpload.albumLabel")}</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              style={{ ...styles.select, flex: 1 }}
              value={selectedAlbumId}
              onChange={(e) => setSelectedAlbumId(e.target.value)}
              disabled={!selectedGalleryId || albums.length === 0}
            >
              <option value="">
                {!selectedGalleryId
                  ? t("autoUpload.selectGalleryFirstPlaceholder")
                  : albums.length === 0
                  ? t("autoUpload.noAlbumsPlaceholder")
                  : t("autoUpload.selectAlbumPlaceholder")}
              </option>
              {albums.map((a) => (
                <option key={a.Id} value={a.Id}>{a.Name} ({t("autoUpload.filesUnit", { count: a.ImageCount || 0 })})</option>
              ))}
            </select>
            {selectedGalleryId && (
              <Button
                variant="secondary"
                onClick={() => setShowCreateAlbum(true)}
                style={styles.inlineCreateBtn}
                title={t("autoUpload.createAlbumTitle")}
              >
                {t("autoUpload.newButton")}
              </Button>
            )}
          </div>
        </div>

        {/* Interval Selection */}
        <div style={styles.field}>
          <label style={styles.label}>{t("autoUpload.scanIntervalLabel")}</label>
          <select
            style={styles.select}
            value={intervalMs}
            onChange={(e) => setIntervalMs(Number(e.target.value))}
          >
            {intervalOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <Button onClick={handleStart} disabled={!canStart} icon={Rocket} style={{ marginTop: 8 }}>
          {t("autoUpload.startButton")}
        </Button>
      </div>

      {/* Active Jobs */}
      {jobList.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h3 style={styles.cardTitle}>{t("autoUpload.activeJobsTitle", { count: jobList.length })}</h3>

          {jobList.map((job) => (
            <div key={job.id} style={styles.jobCard}>
              {/* Confirm Remove Dialog */}
              {confirmRemoveId === job.id && (
                <div style={styles.confirmOverlay} onClick={() => setConfirmRemoveId(null)}>
                  <div style={styles.confirmBox} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: 8, color: "#d97706" }}><AlertTriangle size={26} strokeWidth={1.75} /></div>
                    <h4 style={{ margin: "0 0 8px" }}>{t("autoUpload.removeJobTitle")}</h4>
                    <p style={{ margin: "0 0 16px", fontSize: 13, color: "#666" }}>
                      {t("autoUpload.removeJobMessage", { folder: job.folderName })}
                    </p>
                    <div style={{ display: "flex", gap: 10 }}>
                      <Button variant="secondary" onClick={() => setConfirmRemoveId(null)} style={{ flex: 1 }}>
                        {t("autoUpload.keep")}
                      </Button>
                      <Button onClick={() => { removeJob(job.id); setConfirmRemoveId(null); }} style={{ flex: 1, background: "#e53935" }}>
                        {t("autoUpload.remove")}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Job Header */}
              <div style={styles.jobHeader}>
                <div>
                  <div style={styles.jobTitle}>
                    <span style={{
                      display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                      background: job.status === "running" ? "#22c55e" : job.status === "needs-permission" ? "#ef4444" : "#eab308",
                      marginRight: 8,
                    }} />
                    {job.jobName}
                  </div>
                  <div style={styles.jobSubtitle}>
                    <FolderOpen size={12} strokeWidth={2} style={{ verticalAlign: -2, marginRight: 4 }} />
                    {job.folderName} → {job.gallery.Name} / {job.album.Name}
                  </div>
                </div>
                <div style={styles.jobActions}>
                  {job.status === "running" && (
                    <>
                      <button
                        style={styles.actionBtn}
                        onClick={() => scanNow(job.id)}
                        disabled={job.scanning}
                        title={t("autoUpload.scanNow")}
                      >
                        {job.scanning ? <Clock size={13} strokeWidth={2.25} /> : <RefreshCw size={13} strokeWidth={2.25} />}
                      </button>
                      <button style={styles.actionBtn} onClick={() => pauseJob(job.id)} title={t("autoUpload.pause")}>
                        ⏸️
                      </button>
                    </>
                  )}
                  {(job.status === "paused" || job.status === "needs-permission") && (
                    <button style={styles.actionBtn} onClick={() => resumeJob(job.id)} title={t("autoUpload.resume")}>
                      ▶️
                    </button>
                  )}
                  <button style={styles.actionBtn} onClick={() => setEditingJob(job)} title={t("autoUpload.edit")}>
                    <Pencil size={13} strokeWidth={2.25} />
                  </button>
                  <button
                    style={{ ...styles.actionBtn, color: "#e53935" }}
                    onClick={() => setConfirmRemoveId(job.id)}
                    title={t("autoUpload.remove")}
                  >
                    <Trash2 size={13} strokeWidth={2.25} />
                  </button>
                </div>
              </div>

              {/* Job Stats */}
              <div style={styles.jobStats}>
                <div style={styles.stat}>
                  <span style={styles.statLabel}>{t("autoUpload.stats.interval")}</span>
                  <span style={styles.statValue}>{formatInterval(t, job.intervalMs)}</span>
                </div>
                <div style={styles.stat}>
                  <span style={styles.statLabel}>{t("autoUpload.stats.lastScan")}</span>
                  <span style={styles.statValue}>{formatTime(job.lastScanTime)}</span>
                </div>
                <div style={styles.stat}>
                  <span style={styles.statLabel}>{t("autoUpload.stats.nextScan")}</span>
                  <span style={styles.statValue}>
                    {job.status === "running" ? (getNextScanIn(job) || t("autoUpload.nextScanSoon")) : "—"}
                  </span>
                </div>
                <div style={styles.stat}>
                  <span style={styles.statLabel}>{t("autoUpload.stats.newLastScan")}</span>
                  <span style={styles.statValue}>{job.lastScanNewFiles ?? "—"}</span>
                </div>
                <div style={styles.stat}>
                  <span style={styles.statLabel}>{t("autoUpload.stats.totalUploaded")}</span>
                  <span style={styles.statValue}>{job.totalUploaded}</span>
                </div>
              </div>

              {/* Status */}
              <div style={styles.jobStatus}>
                {job.scanning && <span style={styles.scanPulse}>●</span>}
                <span style={{ color: "#666", fontSize: 13 }}>{job.statusText}</span>
              </div>

              {/* Live Upload Progress */}
              {(() => {
                // Find upload sessions belonging to this job that still have active files
                const jobSessions = Object.values(sessions).filter(
                  (s) => s.id && s.id.startsWith(`auto-${job.id}-`) && s.started &&
                    (s.fileItems || []).some((f) =>
                      ["pending", "checking", "compressing", "uploading", "retrying"].includes(f.status)
                    )
                );
                if (jobSessions.length === 0 || hiddenProgress[job.id]) {
                  // Reset hidden flag if new sessions appear
                  if (jobSessions.length > 0 && hiddenProgress[job.id]) {
                    const hasActive = jobSessions.some((s) =>
                      (s.fileItems || []).some((f) =>
                        ["pending", "checking", "compressing", "uploading", "retrying"].includes(f.status)
                      )
                    );
                    if (hasActive) {
                      setHiddenProgress((p) => { const n = { ...p }; delete n[job.id]; return n; });
                      // fall through to render
                    } else {
                      return null;
                    }
                  } else {
                    return null;
                  }
                }

                const allFiles = jobSessions.flatMap((s) => s.fileItems || []);
                const activeFiles = allFiles.filter((f) =>
                  ["pending", "checking", "compressing", "uploading", "retrying"].includes(f.status)
                );
                const doneFiles = allFiles.filter((f) => f.status === "done");
                const failedFiles = allFiles.filter((f) => f.status === "failed");
                const allDone = allFiles.length > 0 && allFiles.every((f) => f.status === "done" || f.status === "failed");
                const overallPct = allFiles.length > 0
                  ? Math.round((doneFiles.length / allFiles.length) * 100)
                  : 0;

                // Auto-hide 3s after all done
                if (allDone && !hiddenProgress[job.id]) {
                  setTimeout(() => setHiddenProgress((p) => ({ ...p, [job.id]: true })), 3000);
                }

                return (
                  <div style={styles.progressSection}>
                    {/* Overall progress bar */}
                    <div style={styles.overallRow}>
                      <span style={{ fontSize: 12, color: "#666", fontWeight: 600 }}>
                        {t("autoUpload.progressFilesCount", { done: doneFiles.length, total: allFiles.length })}
                        {failedFiles.length > 0 && t("autoUpload.progressFailedCount", { count: failedFiles.length })}
                      </span>
                      <span style={{ fontSize: 12, color: "#6c63ff", fontWeight: 700 }}>{overallPct}%</span>
                    </div>
                    <div style={styles.overallBarBg}>
                      <div style={{ ...styles.overallBarFill, width: `${overallPct}%` }} />
                    </div>

                    {/* Active file details */}
                    {activeFiles.slice(0, 5).map((item) => (
                      <div key={item.id} style={styles.fileRow}>
                        <span style={{ display: "inline-flex" }}><UploadStatusIcon status={item.status} fileType={item.fileType} size={14} /></span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={styles.fileName}>{item.fileName}</div>
                          <div style={styles.fileInfo}>
                            {item.originalSize
                              ? <>{formatFileSize(item.originalSize)}<span style={{ color: "#bbb" }}> → </span><span style={{ color: "#16a34a", fontWeight: 700 }}>{formatFileSize(item.fileSize)}</span>{item.compressSec && <span style={{ color: "#9ca3af" }}> ({item.compressSec}s)</span>}</>
                              : formatFileSize(item.fileSize)
                            }{" — "}{fileStatusText(t, item)}
                          </div>
                          {(item.status === "uploading" || item.status === "compressing") && (
                            <div style={styles.fileBarBg}>
                              <div style={{
                                ...styles.fileBarFill,
                                width: `${item.status === "compressing" ? item.compressProgress : item.uploadProgress}%`,
                                background: item.status === "compressing" ? "#f59e0b" : "#6c63ff",
                              }} />
                            </div>
                          )}
                          {item.compressEngine && (
                            <span style={{
                              display: "inline-block",
                              marginTop: 2,
                              fontSize: 10,
                              fontWeight: 600,
                              color: item.compressEngine === "libx264" ? "#888" : "#6c63ff",
                              background: item.compressEngine === "libx264" ? "#f3f4f6" : "#ede9fe",
                              borderRadius: 4,
                              padding: "1px 6px",
                            }}>
                              {encoderLabel(item.compressEngine)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    {activeFiles.length > 5 && (
                      <div style={{ fontSize: 12, color: "#888", padding: "4px 0" }}>
                        {t("autoUpload.filesRemaining", { count: activeFiles.length - 5 })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {jobList.length === 0 && (
        <div style={styles.emptyState}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 12, color: "#c7c9d9" }}><FolderOpen size={38} strokeWidth={1.5} /></div>
          <p style={{ margin: 0, color: "#888", fontSize: 14 }}>
            {t("autoUpload.emptyState")}
          </p>
        </div>
      )}

      {/* Create Gallery Modal */}
      {showCreateGallery && (
        <CreateGalleryModal
          eventId={activeSourceId}
          onClose={() => setShowCreateGallery(false)}
          onCreated={(newGallery) => {
            // Refetch galleries and auto-select the new one
            setLoadingGalleries(true);
            getGalleriesByEvent(activeSourceId)
              .then((res) => {
                const updated = res.data?.Result || [];
                setGalleries(updated);
                // Find the newly created gallery and select it
                const created = updated.find((g) => g.Name === newGallery?.Name) || updated[updated.length - 1];
                if (created) setSelectedGalleryId(String(created.Id));
              })
              .catch(() => toast.error(t("autoUpload.toasts.failedRefreshGalleries")))
              .finally(() => setLoadingGalleries(false));
          }}
        />
      )}

      {/* Create Album Modal */}
      {showCreateAlbum && selectedGallery && (
        <CreateAlbumModal
          galleryId={selectedGallery.Id}
          galleryName={selectedGallery.Name}
          onClose={() => setShowCreateAlbum(false)}
          onCreated={(newAlbum) => {
            // Refetch galleries to get updated album list and auto-select the new album
            setLoadingGalleries(true);
            getGalleriesByEvent(activeSourceId)
              .then((res) => {
                const updated = res.data?.Result || [];
                setGalleries(updated);
                const updatedGallery = updated.find((g) => String(g.Id) === String(selectedGalleryId));
                if (updatedGallery) {
                  const created = (updatedGallery.Albums || []).find((a) => a.Name === newAlbum?.Name)
                    || (updatedGallery.Albums || [])[(updatedGallery.Albums || []).length - 1];
                  if (created) setSelectedAlbumId(String(created.Id));
                }
              })
              .catch(() => toast.error(t("autoUpload.toasts.failedRefreshAlbums")))
              .finally(() => setLoadingGalleries(false));
          }}
        />
      )}

      {/* Edit Job Modal */}
      {editingJob && (
        <EditJobModal
          job={editingJob}
          onClose={() => setEditingJob(null)}
          onSave={(updates) => {
            updateJob(updates);
            setEditingJob(null);
          }}
        />
      )}
    </div>
    </>
  );
};

/* ─── Styles ─── */
const styles = {
  heading: { margin: "0 0 8px", fontSize: 22, fontWeight: 700, color: "#1e1e2d" },
  description: { margin: "0 0 24px", fontSize: 14, color: "#666", lineHeight: 1.6 },

  setupCard: {
    background: "#fff", borderRadius: 12, padding: 28,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)", maxWidth: 560,
  },
  cardTitle: { margin: "0 0 20px", fontSize: 16, fontWeight: 700, color: "#1e1e2d" },

  field: { marginBottom: 18 },
  label: { display: "block", fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 6 },
  select: {
    width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e0e0e0",
    fontSize: 14, color: "#333", background: "#fafafa", outline: "none",
    cursor: "pointer", appearance: "auto",
  },
  folderName: {
    fontSize: 14, color: "#6c63ff", fontWeight: 600,
    padding: "6px 12px", background: "#f3f0ff", borderRadius: 8,
  },
  inlineCreateBtn: {
    padding: "8px 12px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
    flexShrink: 0,
  },
  toggleGroup: {
    display: "flex", gap: 0, borderRadius: 8, overflow: "hidden",
    border: "1.5px solid #e0e0e0", width: "fit-content",
  },
  toggleBtn: {
    padding: "9px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer",
    border: "none", background: "#fafafa", color: "#666",
    transition: "all 0.15s ease",
  },
  toggleBtnActive: {
    background: "#6c63ff", color: "#fff",
  },

  warningCard: {
    background: "#fffbeb", border: "1.5px solid #fbbf24", borderRadius: 12,
    padding: 28, maxWidth: 500, textAlign: "center",
  },

  jobCard: {
    position: "relative", background: "#fff", borderRadius: 12, padding: 20,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: 16,
  },
  jobHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16,
  },
  jobTitle: { fontSize: 15, fontWeight: 700, color: "#1e1e2d", display: "flex", alignItems: "center" },
  jobSubtitle: { fontSize: 13, color: "#888", marginTop: 4, paddingLeft: 16 },
  jobActions: { display: "flex", gap: 6 },
  actionBtn: {
    background: "none", border: "1px solid #e5e7eb", borderRadius: 8,
    padding: "6px 10px", cursor: "pointer", fontSize: 16, lineHeight: 1,
  },

  jobStats: {
    display: "flex", gap: 16, flexWrap: "wrap", padding: "12px 0",
    borderTop: "1px solid #f0f0f0", borderBottom: "1px solid #f0f0f0",
  },
  stat: { display: "flex", flexDirection: "column", gap: 2, minWidth: 80 },
  statLabel: { fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5 },
  statValue: { fontSize: 14, fontWeight: 600, color: "#1e1e2d" },

  jobStatus: { display: "flex", alignItems: "center", gap: 8, marginTop: 12 },
  scanPulse: { color: "#22c55e", animation: "pulse 1.5s infinite", fontSize: 12 },

  confirmOverlay: {
    position: "absolute", inset: 0, background: "rgba(255,255,255,0.9)",
    display: "flex", alignItems: "center", justifyContent: "center",
    borderRadius: 12, zIndex: 10,
  },
  confirmBox: {
    background: "#fff", borderRadius: 12, padding: 24,
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)", maxWidth: 320, textAlign: "center",
  },

  emptyState: {
    textAlign: "center", padding: "48px 0",
  },

  /* Upload progress styles */
  progressSection: {
    marginTop: 14, padding: "14px 0 0",
    borderTop: "1px solid #f0f0f0",
  },
  overallRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6,
  },
  overallBarBg: {
    height: 6, borderRadius: 3, background: "#f0f0f0", overflow: "hidden", marginBottom: 12,
  },
  overallBarFill: {
    height: "100%", borderRadius: 3, background: "#6c63ff",
    transition: "width 0.3s ease",
  },
  fileRow: {
    display: "flex", gap: 10, alignItems: "flex-start", padding: "6px 0",
    borderBottom: "1px solid #fafafa",
  },
  fileName: {
    fontSize: 13, fontWeight: 600, color: "#333",
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  },
  fileInfo: { fontSize: 12, color: "#888", marginTop: 2 },
  fileBarBg: {
    height: 4, borderRadius: 2, background: "#f0f0f0", overflow: "hidden", marginTop: 4,
  },
  fileBarFill: {
    height: "100%", borderRadius: 2, transition: "width 0.3s ease",
  },
};

export default AutoUpload;
